import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV, SEVERITY_COLORS } from './nav';

interface Violation {
    id: number; rule_key: string; title: string; description: string; severity: string; status: string;
    assigned_to: string | null; assigned_to_id: number | null; resolution_notes: string;
    raised_at: string; resolved_at: string | null;
}
interface Agent { id: number; full_name: string; }

const STATUS_COLORS: Record<string, string> = {
    open: 'danger', investigating: 'warning', remediated: 'success', accepted_risk: 'info', false_positive: 'secondary',
};
const STATUSES = ['open', 'investigating', 'remediated', 'accepted_risk', 'false_positive'];

const ViolationFlags: React.FC = () => {
    const { api } = useInternalApi();
    const [violations, setViolations] = useState<Violation[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', severity: 'medium' });
    const [viewing, setViewing] = useState<Violation | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => { document.title = 'Violation Flags - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const res = await api.get(`compliance-api/violations/?${params}`);
            if (res.data?.status === 'success') setViolations(res.data.data || []);
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('compliance-api/agents/').then(r => { if (r.data?.status === 'success') setAgents(r.data.data || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitCreate = async () => {
        if (!form.title.trim() || !form.description.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Title and description are required.' });
            return;
        }
        try {
            await api.post('compliance-api/violations/', form);
            setShowCreate(false);
            setForm({ title: '', description: '', severity: 'medium' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not raise flag.' });
        }
    };

    const patchViolation = async (v: Violation, payload: any) => {
        try {
            const res = await api.patch(`compliance-api/violations/${v.id}/`, payload);
            if (res.data?.status === 'success') {
                if (viewing?.id === v.id) setViewing(res.data.data);
                load();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update flag.' });
        }
    };

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFlag} className="me-2 text-primary" />Violation Flags</h2>
                    <p className="text-muted mb-0">Compliance findings escalated into investigable cases</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Raise Flag</button>
            </div>

            <select className="form-select mb-3" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : violations.length === 0 ? (
                        <div className="text-center py-5 text-muted">No violation flags raised.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Title</th><th>Rule</th><th>Severity</th><th>Status</th><th>Assigned</th><th>Raised</th></tr></thead>
                            <tbody>
                                {violations.map(v => (
                                    <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => { setViewing(v); setNotes(v.resolution_notes); }}>
                                        <td>{v.title}</td>
                                        <td className="small text-muted">{v.rule_key || '—'}</td>
                                        <td><span className={`badge bg-${SEVERITY_COLORS[v.severity]}`}>{v.severity}</span></td>
                                        <td><span className={`badge bg-${STATUS_COLORS[v.status]}`}>{v.status.replace('_', ' ')}</span></td>
                                        <td>{v.assigned_to || <span className="text-muted">Unassigned</span>}</td>
                                        <td className="small">{new Date(v.raised_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Raise Violation Flag</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input className="form-control" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Severity</label>
                                    <select className="form-select" value={form.severity} onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))}>
                                        <option value="low">Low</option><option value="medium">Medium</option>
                                        <option value="high">High</option><option value="critical">Critical</option>
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Raise Flag</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.title}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-flex gap-2 mb-3">
                                    <span className={`badge bg-${SEVERITY_COLORS[viewing.severity]}`}>{viewing.severity}</span>
                                    <span className={`badge bg-${STATUS_COLORS[viewing.status]}`}>{viewing.status.replace('_', ' ')}</span>
                                    {viewing.rule_key && <span className="badge bg-light text-dark">{viewing.rule_key}</span>}
                                </div>
                                <p>{viewing.description}</p>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label small">Status</label>
                                        <select className="form-select form-select-sm" value={viewing.status} onChange={(e) => patchViolation(viewing, { status: e.target.value })}>
                                            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small">Assigned to</label>
                                        <select className="form-select form-select-sm" value={viewing.assigned_to_id || ''} onChange={(e) => patchViolation(viewing, { assigned_to_id: e.target.value || null })}>
                                            <option value="">Unassigned</option>
                                            {agents.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <label className="form-label small">Resolution notes</label>
                                <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                                {viewing.resolved_at && <div className="form-text">Resolved {new Date(viewing.resolved_at).toLocaleString()}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                <button className="btn btn-primary" onClick={() => patchViolation(viewing, { resolution_notes: notes })}>Save Notes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default ViolationFlags;
